import * as React from "react"
import "./heatmap-flight-view.css"
import cn from "classnames"

import { useHeatmapState, SelectionChangeEventHandler, HeatmapState } from "./heatmap-state"

export type { SelectionChangeEventHandler }

export type HeatmapStyle = {
    color: string
    selectedColor?: string
    disabled: boolean
    raised: boolean
    icon?: React.FunctionComponent<React.SVGAttributes<SVGElement>>
    state: string
    width?: string
}

/**
 * One tile in the heatmap
 */
const HeatmapTile = React.memo(
    ({ id, selected, style }: { id: string; selected: boolean; style: HeatmapStyle }) => (
        <li
            role="option"
            tabIndex={style.disabled ? undefined : 0}
            style={
                {
                    "--heatmap-tile-bg": (selected && style.selectedColor) || style.color,
                    width: style.width || "26px",
                } as React.CSSProperties
            }
            data-id={id}
            data-testid={id}
            aria-selected={selected}
            aria-disabled={style.disabled}
            className={cn("heatmap-tile-deprecated", {
                "heatmap-tile--raised": style.raised,
            })}
        >
            {style.icon && <style.icon className="heatmap-tile__icon" role="presentation" />}
        </li>
    )
)

export type HeatmapProps<T extends { id: string }> = {
    items: T[]
    style: (instance: T) => HeatmapStyle
    selected: HeatmapState["selected"]
    updateSelected: (data: Record<string, boolean>) => void
}

function isDisabled(t: HTMLElement | null) {
    return !t || t.getAttribute("aria-disabled") === "true"
}

function focusElement(t: Element | null) {
    if (t && "focus" in t && typeof t.focus === "function") {
        t.focus()
    }
}

export const HeatmapFlightView = <T extends { id: string }>({
    items,
    style,
    selected,
    updateSelected,
}: HeatmapProps<T>) => {
    const { toggleSelection, select, deselect, setSelected } = useHeatmapState(
        selected,
        updateSelected
    )

    const [lastShiftSelected, setLastShiftSelected] = React.useState<number | null>(null)

    const onSelect = (e: React.MouseEvent | React.KeyboardEvent, t: HTMLLIElement) => {
        const tiles = getTiles()
        const i = tiles.indexOf(t)
        setLastShiftSelected(i)
        if (e.ctrlKey || e.metaKey) {
            toggle(tiles[i], true)
        } else if (e.shiftKey) {
            if (lastShiftSelected !== null) {
                const ids = []
                for (
                    let num = Math.min(i, lastShiftSelected);
                    num < Math.max(i, lastShiftSelected);
                    num++
                ) {
                    const id = tiles[num].dataset["id"]
                    if (id) ids.push(id)
                }
                select(ids)
            } else {
                if (t.dataset.id) {
                    select([t.dataset.id])
                }
            }
        } else {
            toggle(t, e.shiftKey)
        }
    }

    const onClick = (e: React.MouseEvent) => {
        onSelect(e, e.target as HTMLLIElement)
    }

    const heatmapRef = React.useRef<HTMLUListElement>(null)
    // this grabs the list of tiles not from props but by querying the DOM, which lets us do things like focus a tile and similar without going back through React
    // it also automatically ignores disabled values
    const getTiles = () =>
        Array.from(
            heatmapRef.current?.querySelectorAll("li.heatmap-tile[aria-disabled=false]") || []
        ) as Array<HTMLLIElement>

    // this grabs the number of rows to skip
    const getRowLength = () => {
        const width = Math.max(28 * 5, heatmapRef.current?.getBoundingClientRect()?.width || 0) + 2
        return Math.floor(width / 28) // this comes from the CSS
    }

    const collectIds = (tiles: Array<HTMLLIElement>, from: number, to: number) => {
        const ids = [] as string[]
        for (let i = from; i <= to; i++) {
            const id = tiles[i]?.dataset["id"]
            if (id) ids.push(id)
        }
        return ids
    }

    const toggle = (t: HTMLElement | null, isAdditive: boolean) => {
        if (!t) {
            return
        }
        if (isDisabled(t)) return
        const id = t.dataset["id"]
        if (id != null) {
            if (isAdditive || (Object.keys(selected).length === 1 && selected[id])) {
                toggleSelection(id)
            } else {
                const test: Record<string, boolean> = {}
                test[id] = true
                setSelected(test)
            }
        }
    }

    const onKeyDown = (e: React.KeyboardEvent) => {
        const t = e.target as HTMLLIElement
        // these are all the keyboard interactions
        // following the recommended keyboard shortcuts in https://www.w3.org/WAI/ARIA/apg/patterns/grid/
        const action = {
            // selection
            " ": () => {
                onSelect(e, t)
            },
            home: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                const rowlen = getRowLength()
                if (e.ctrlKey || e.metaKey) {
                    focusElement(tiles[0])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, 0, i)
                        select(ids)
                    }
                } else {
                    focusElement(tiles[i - (i % rowlen)])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, i - (i % rowlen), i)
                        select(ids)
                    }
                }
            },
            end: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                const rowlen = getRowLength()

                if (e.ctrlKey || e.metaKey) {
                    focusElement(tiles[tiles.length - 1])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, i, tiles.length - 1)
                        select(ids)
                    }
                } else {
                    const idx = Math.min(tiles.length - 1, i + (rowlen - (i % rowlen)) - 1)
                    focusElement(tiles[idx])
                    if (e.shiftKey) {
                        const ids = collectIds(tiles, i, idx)
                        select(ids)
                    }
                }
            },
            arrowright: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                if (i < tiles.length - 1) {
                    const next = tiles[i + 1]
                    focusElement(next)
                    if (e.shiftKey) toggle(next as HTMLElement, true)
                }
            },
            arrowleft: () => {
                const tiles = getTiles()
                const i = tiles.indexOf(t)
                if (i > 0) {
                    const prev = tiles[i - 1]
                    focusElement(prev)
                    if (e.shiftKey) toggle(prev as HTMLElement, true)
                }
            },
            arrowdown: () => {
                // big advance
                const rowlen = getRowLength()
                const tiles = getTiles()
                const startIdx = tiles.indexOf(t)
                const endIdx = startIdx + rowlen
                if (startIdx + rowlen >= tiles.length) {
                    return
                }
                const destination = tiles[endIdx]
                focusElement(destination)
                if (e.shiftKey) {
                    const ids = collectIds(tiles, startIdx, endIdx)
                    select(ids)
                }
            },
            arrowup: () => {
                // big retreat
                const rowlen = getRowLength()
                const tiles = getTiles()
                const endIdx = tiles.indexOf(t)
                const startIdx = endIdx - rowlen

                if (startIdx < 0) {
                    return
                }
                const destination = tiles[startIdx]
                focusElement(destination)
                if (e.shiftKey) {
                    const ids = collectIds(tiles, startIdx, endIdx)
                    select(ids)
                }
            },
            a: () => {
                if (e.ctrlKey || e.metaKey) {
                    const tiles = getTiles()
                    const ids = collectIds(tiles, 0, tiles.length - 1)
                    select(ids)
                }
            },
        }[e.key.toLowerCase()]

        if (action) {
            action()
            e.stopPropagation()
        }
    }

    // this effect handles the situation where a selected item transitions to be unselectable
    // in this case, it needs to be deselected
    React.useEffect(() => {
        const ids = [] as string[]
        for (const i of items) {
            if (style(i).disabled && selected[i.id]) {
                ids.push(i.id)
            }
        }
        // handles the case of items being removed by a parent component
        // if an item is removed from the list, it should not be selected
        for (const [key, value] of Object.entries(selected)) {
            if (!value) continue
            if (!items.find((item) => item.id === key)) {
                ids.push(key)
            }
        }
        deselect(ids)
    }, [items, selected, deselect, toggleSelection, style])

    return (
        <ul
            className="heatmap-deprecated"
            role="grid"
            ref={heatmapRef}
            aria-multiselectable="true"
            onClick={onClick}
            onKeyDown={onKeyDown}
        >
            {items.map((i, idx) => (
                <HeatmapTile id={i.id} key={idx} selected={!!selected[i.id]} style={style(i)} />
            ))}
        </ul>
    )
}
